import { createRng, nextInt, pickWeighted, type RngState } from './rng';
import { buySkill, isSkillId, skillNode, type SkillLevels } from './skills';
import {
  ARCHETYPES,
  GEAR_RARITIES,
  GEAR_SLOTS,
  HERO_IDS,
  SimError,
  type ArchetypeId,
  type Balance,
  type GearItem,
  type GearRarity,
  type GearSlot,
  type HeroGearMods,
  type HeroId,
  type SkillId,
  type SkinId,
} from './types';

/**
 * The player's persistent progress. Lives on the server; the client only displays it.
 * Nothing here has a way out into money: copies and gear drop from runs, levels raise base stats.
 */
export interface CardProgress {
  level: number;
  /** Copies collected toward the next level. */
  copies: number;
}

export interface HeroProgress {
  /** Shared across heroes: one squad level. */
  level: number;
  xp: number;
  /** Which hero walks out of the bunker. */
  selected: HeroId;
  inventory: GearItem[];
  /** Item id per slot. */
  equipped: Partial<Record<GearSlot, number>>;
  nextItemId: number;
}

export interface MapProgress {
  won: boolean;
  bestWaves: number;
  runs: number;
  /** Best star rating on a win, 0 until won. */
  stars: number;
}

export interface ShopProgress {
  /** When the player declared being 18+, or null. Chests stay locked until then. */
  adultAt: number | null;
  chestsOpened: number;
  /** Chests opened since the last epic item; the pity guarantee reads this. */
  pitySince: number;
  /** UTC day of the last free chest, or null. */
  lastFreeChestDay: string | null;
  starsSpent: number;
  /** The one-time starter pack has been bought. */
  starterBought: boolean;
  /** Cosmetic skins owned (the default is implicit) and the one in use. */
  skins: SkinId[];
  skin: SkinId;
}

export interface QuestProgress {
  /** Template index in balance.quests.pool plus the map for win_map, e.g. "9:map01". */
  id: string;
  progress: number;
  claimed: boolean;
}

export interface QuestsProgress {
  day: string;
  items: QuestProgress[];
}

export interface Profile {
  v: 1;
  cards: Record<ArchetypeId, CardProgress>;
  hero: HeroProgress;
  maps: Record<string, MapProgress>;
  shop: ShopProgress;
  quests: QuestsProgress;
  /** Skill tree currency: medals from rewarded runs. */
  medals: number;
  /** Bought skill levels; absent = 0. */
  skills: SkillLevels;
  /** Consecutive UTC days with an accepted run; `last` is the latest such day. */
  streak: { last: string | null; count: number };
  /** Weekly challenges completed this ISO week. */
  challenges: { week: string; done: string[] };
  /** Who invited this player (set once, at the first visit), whether the invite paid out, and how many friends this player brought. */
  referral: { by: number | null; claimed: boolean; invites: number };
}

/**
 * The part of the profile a run depends on. The client sends it with the submission and the
 * server replays with it after checking it does not exceed the stored profile, so a run that
 * started before a reward landed still verifies. Gear is listed as full items: the server checks
 * each one is owned.
 */
export interface ProfileSnapshot {
  cardLevels: Record<ArchetypeId, number>;
  heroLevel: number;
  heroId: HeroId;
  heroGear: GearItem[];
  skills: SkillLevels;
}

export interface RunRewards {
  copies: Partial<Record<ArchetypeId, number>>;
  heroXp: number;
  /** Skill tree currency; chests and quests may omit it. */
  medals?: number;
  /** Rolled item without an id; applyRewards numbers it. */
  gear: Omit<GearItem, 'id'> | null;
}

export interface RewardOutcome {
  cardLevelUps: { arch: ArchetypeId; level: number }[];
  /** New hero level, or null when the hero did not level up. */
  heroLevelUp: number | null;
  /** The item as stored, or null when nothing dropped or the bag was full. */
  gear: GearItem | null;
  gearLost: boolean;
}

function allOnes(): Record<ArchetypeId, number> {
  const out = {} as Record<ArchetypeId, number>;
  for (const a of ARCHETYPES) out[a] = 1;
  return out;
}

export function defaultProfile(): Profile {
  const cards = {} as Record<ArchetypeId, CardProgress>;
  for (const a of ARCHETYPES) cards[a] = { level: 1, copies: 0 };
  return {
    v: 1,
    cards,
    hero: { level: 1, xp: 0, selected: 'commander', inventory: [], equipped: {}, nextItemId: 1 },
    maps: {},
    shop: { adultAt: null, chestsOpened: 0, pitySince: 0, lastFreeChestDay: null, starsSpent: 0, starterBought: false, skins: [], skin: 'default' },
    quests: { day: '', items: [] },
    medals: 0,
    skills: {},
    streak: { last: null, count: 0 },
    challenges: { week: '', done: [] },
    referral: { by: null, claimed: false, invites: 0 },
  };
}

/** Fills fields older stored profiles lack. Safe to call on a fresh one. */
export function migrateProfile(p: Profile): Profile {
  const fresh = defaultProfile();
  for (const a of ARCHETYPES) p.cards[a] ??= { level: 1, copies: 0 };
  p.hero ??= fresh.hero;
  p.hero.selected ??= 'commander';
  p.hero.inventory ??= [];
  p.hero.equipped ??= {};
  p.hero.nextItemId ??= p.hero.inventory.reduce((m, it) => Math.max(m, it.id), 0) + 1;
  p.maps ??= {};
  p.shop ??= fresh.shop;
  p.shop.starterBought ??= false;
  p.shop.skins ??= [];
  p.shop.skin ??= 'default';
  p.quests ??= fresh.quests;
  p.medals ??= 0;
  p.skills ??= {};
  p.streak ??= { last: null, count: 0 };
  p.challenges ??= { week: '', done: [] };
  p.referral ??= { by: null, claimed: false, invites: 0 };
  return p;
}

/** Parse a Mini App start parameter of the form "r<userId>"; null for anything else. */
export function referrerFromStartParam(param: string | null | undefined): number | null {
  if (!param) return null;
  const m = /^r(\d{1,12})$/.exec(param);
  return m ? Number(m[1]) : null;
}

/**
 * Parse an acquisition-source start parameter of the form "s_<label>", label = [a-z0-9_-]{1,32} in any case;
 * returns the lower-cased label, or null for anything else (including the referral form "r<id>").
 * Campaign links look like https://t.me/<bot>?startapp=s_ag_ua.
 */
export function sourceFromStartParam(param: string | null | undefined): string | null {
  if (!param) return null;
  const m = /^s_([a-z0-9_-]{1,32})$/i.exec(param);
  const label = m ? m[1]!.toLowerCase() : null;
  // 'organic' is the display name of the NULL bucket in source stats; a campaign may not claim it.
  return label === 'organic' ? null : label;
}

/** Remember who invited a brand-new player. Refuses self-invites and never overwrites an existing referrer. */
export function attributeReferral(p: Profile, selfId: number, referrerId: number | null): boolean {
  if (referrerId === null || referrerId === selfId || p.referral.by !== null) return false;
  p.referral.by = referrerId;
  return true;
}

/**
 * The invited player cleared enough waves: pay the invitee once. Returns the medals paid, or 0.
 * The referrer's share is paid by the caller, who has to load the other profile.
 */
export function claimReferral(p: Profile, wavesCleared: number, balance: Balance): number {
  if (p.referral.by === null || p.referral.claimed || wavesCleared < balance.referral.goalWaves) return 0;
  p.referral.claimed = true;
  p.medals = (p.medals ?? 0) + balance.referral.medalsEach;
  return balance.referral.medalsEach;
}

/** Credit the referrer for one confirmed friend; capped so a farm of alts cannot mint medals forever. */
export function creditReferrer(p: Profile, balance: Balance): number {
  if (p.referral.invites >= balance.referral.maxInvites) return 0;
  p.referral.invites++;
  p.medals = (p.medals ?? 0) + balance.referral.medalsEach;
  return balance.referral.medalsEach;
}

/** Medals the streak pays on its n-th consecutive day (n >= 1). */
export function streakMedals(dayNumber: number, balance: Balance): number {
  const t = balance.streak.medals;
  return t[Math.min(Math.max(1, dayNumber), t.length) - 1] ?? 0;
}

/**
 * Record that the player played on `day` (UTC, YYYY-MM-DD). The first run of a day extends the streak
 * when yesterday was played, restarts it otherwise, and pays medals; later runs that day change nothing.
 */
export function touchStreak(p: Profile, day: string, yesterday: string, balance: Balance): { count: number; medals: number } | null {
  if (p.streak.last === day) return null;
  p.streak.count = p.streak.last === yesterday ? p.streak.count + 1 : 1;
  p.streak.last = day;
  const medals = streakMedals(p.streak.count, balance);
  p.medals = (p.medals ?? 0) + medals;
  return { count: p.streak.count, medals };
}

/** Raise one skill node by a level, paying medals. SimError codes: unknown_skill, max_level, skill_locked, no_medals. */
export function upgradeSkill(p: Profile, id: SkillId, balance: Balance): { level: number } {
  const r = buySkill(p.skills, p.medals, id, balance);
  p.medals = r.medals;
  return { level: r.level };
}

export function defaultSnapshot(): ProfileSnapshot {
  return { cardLevels: allOnes(), heroLevel: 1, heroId: 'commander', heroGear: [], skills: {} };
}

export function heroUnlocked(p: Profile, id: HeroId, balance: Balance): boolean {
  return p.hero.level >= balance.heroes[id].unlockLevel;
}

export function selectHero(p: Profile, id: HeroId, balance: Balance): void {
  if (!(HERO_IDS as readonly string[]).includes(id)) throw new SimError('unknown_hero', String(id));
  if (!heroUnlocked(p, id, balance)) throw new SimError('hero_locked', `${id} needs level ${balance.heroes[id].unlockLevel}`);
  p.hero.selected = id;
}

/** Items the hero actually wears, resolved from the equipped ids; dangling ids are skipped. */
export function equippedGear(p: Profile): GearItem[] {
  const out: GearItem[] = [];
  for (const slot of GEAR_SLOTS) {
    const id = p.hero.equipped[slot];
    if (id === undefined) continue;
    const item = p.hero.inventory.find((it) => it.id === id && it.slot === slot);
    if (item) out.push(item);
  }
  return out;
}

export function snapshotOf(p: Profile): ProfileSnapshot {
  const cardLevels = {} as Record<ArchetypeId, number>;
  for (const a of ARCHETYPES) cardLevels[a] = p.cards[a]?.level ?? 1;
  return { cardLevels, heroLevel: p.hero.level, heroId: p.hero.selected, heroGear: equippedGear(p).map((it) => ({ ...it })), skills: { ...p.skills } };
}

function sameItem(a: GearItem, b: GearItem): boolean {
  return a.id === b.id && a.slot === b.slot && a.rarity === b.rarity && a.bonus === b.bonus;
}

/** True when every level in the snapshot is at most what the profile owns, the hero is unlocked and every gear item is owned. */
export function snapshotWithin(snap: ProfileSnapshot, p: Profile, balance: Balance): boolean {
  if (snap.heroLevel > p.hero.level) return false;
  if (!heroUnlocked(p, snap.heroId, balance)) return false;
  for (const a of ARCHETYPES) if (snap.cardLevels[a] > (p.cards[a]?.level ?? 1)) return false;
  for (const it of snap.heroGear) if (!p.hero.inventory.some((own) => sameItem(own, it))) return false;
  for (const [id, lv] of Object.entries(snap.skills)) if ((lv ?? 0) > (p.skills[id as SkillId] ?? 0)) return false;
  return true;
}

function isLevel(x: unknown, max: number): x is number {
  return typeof x === 'number' && Number.isInteger(x) && x >= 1 && x <= max;
}

function isSlot(x: unknown): x is GearSlot {
  return typeof x === 'string' && (GEAR_SLOTS as readonly string[]).includes(x);
}

function isRarity(x: unknown): x is GearRarity {
  return typeof x === 'string' && (GEAR_RARITIES as readonly string[]).includes(x);
}

/** Turns untrusted JSON into a snapshot, or null when the shape is wrong. Unknown keys are dropped. */
export function normalizeSnapshot(x: unknown, balance: Balance): ProfileSnapshot | null {
  if (typeof x !== 'object' || x === null) return null;
  const o = x as { cardLevels?: unknown; heroLevel?: unknown; heroGear?: unknown; heroId?: unknown; skills?: unknown };
  if (!isLevel(o.heroLevel, balance.heroMult.length)) return null;
  const heroId: HeroId = o.heroId === undefined ? 'commander' : (o.heroId as HeroId);
  if (!(HERO_IDS as readonly string[]).includes(heroId)) return null;
  if (typeof o.cardLevels !== 'object' || o.cardLevels === null) return null;
  const levels = o.cardLevels as Record<string, unknown>;
  const cardLevels = {} as Record<ArchetypeId, number>;
  for (const a of ARCHETYPES) {
    const v = levels[a];
    if (!isLevel(v, balance.cardMult.length)) return null;
    cardLevels[a] = v;
  }
  const heroGear: GearItem[] = [];
  if (o.heroGear !== undefined) {
    if (!Array.isArray(o.heroGear) || o.heroGear.length > GEAR_SLOTS.length) return null;
    const seen = new Set<GearSlot>();
    for (const raw of o.heroGear as unknown[]) {
      if (typeof raw !== 'object' || raw === null) return null;
      const it = raw as { id?: unknown; slot?: unknown; rarity?: unknown; bonus?: unknown };
      if (!isLevel(it.id, 1_000_000_000) || !isSlot(it.slot) || !isRarity(it.rarity) || !isLevel(it.bonus, 1000)) return null;
      if (seen.has(it.slot)) return null;
      seen.add(it.slot);
      heroGear.push({ id: it.id, slot: it.slot, rarity: it.rarity, bonus: it.bonus });
    }
  }
  const skills: SkillLevels = {};
  if (o.skills !== undefined) {
    if (typeof o.skills !== 'object' || o.skills === null || Array.isArray(o.skills)) return null;
    for (const [id, lv] of Object.entries(o.skills as Record<string, unknown>)) {
      if (!isSkillId(id)) return null;
      if (typeof lv !== 'number' || !Number.isInteger(lv) || lv < 0 || lv > skillNode(id, balance).maxLevel) return null;
      if (lv > 0) skills[id] = lv;
    }
  }
  return { cardLevels, heroLevel: o.heroLevel, heroId, heroGear, skills };
}

export function maxCardLevel(balance: Balance): number {
  return balance.cardMult.length;
}

export function maxHeroLevel(balance: Balance): number {
  return balance.heroMult.length;
}

/** Copies needed to leave `level`, or null at the cap. */
export function cardCopiesToNext(level: number, balance: Balance): number | null {
  if (level >= maxCardLevel(balance)) return null;
  return balance.cardCopies[Math.min(level, balance.cardCopies.length) - 1] ?? null;
}

export function heroXpToNext(level: number, balance: Balance): number | null {
  if (level >= maxHeroLevel(balance)) return null;
  return balance.heroXpCosts[Math.min(level, balance.heroXpCosts.length) - 1] ?? null;
}

// ---------- gear ----------

/** Which set bonuses a loadout has earned. */
export function gearSets(items: readonly GearItem[], balance: Balance): { pair: boolean; fullEpic: boolean } {
  const minIdx = GEAR_RARITIES.indexOf(balance.gear.sets.pairMinRarity);
  const good = items.filter((it) => GEAR_RARITIES.indexOf(it.rarity) >= minIdx).length;
  const epics = items.filter((it) => it.rarity === 'epic').length;
  return { pair: good >= 2, fullEpic: epics >= GEAR_SLOTS.length };
}

/** What the listed items add to the hero, as percent multipliers, set bonuses included. */
export function gearMods(items: readonly GearItem[], balance?: Balance): HeroGearMods {
  const m: HeroGearMods = { hpPct: 100, dmgPct: 100, speedPct: 100, aoePct: 100, aoeEveryDelta: 0 };
  for (const it of items) {
    if (it.slot === 'weapon') m.dmgPct += it.bonus;
    else if (it.slot === 'armor') m.hpPct += it.bonus;
    else if (it.slot === 'boots') m.speedPct += it.bonus;
    else m.aoePct += it.bonus;
  }
  if (balance) {
    const sets = gearSets(items, balance);
    if (sets.pair) {
      m.hpPct += balance.gear.sets.pairPct;
      m.dmgPct += balance.gear.sets.pairPct;
      m.speedPct += balance.gear.sets.pairPct;
      m.aoePct += balance.gear.sets.pairPct;
    }
    if (sets.fullEpic) m.aoeEveryDelta += balance.gear.sets.fullEpicAoeEveryDelta;
  }
  return m;
}

export function nextRarity(r: GearRarity): GearRarity | null {
  const i = GEAR_RARITIES.indexOf(r);
  return GEAR_RARITIES[i + 1] ?? null;
}

/** Rolls one item of the given slot and rarity: bonus uniform in the balance range. */
export function rollGear(rng: RngState, slot: GearSlot, rarity: GearRarity, balance: Balance): Omit<GearItem, 'id'> {
  const [lo, hi] = balance.gear.bonus[slot][rarity];
  return { slot, rarity, bonus: lo + nextInt(rng, hi - lo + 1) };
}

function rollDrop(rng: RngState, won: boolean, balance: Balance): Omit<GearItem, 'id'> {
  const weights = won ? balance.gear.winRarityWeights : balance.gear.rarityWeights;
  const rarity = pickWeighted(
    rng,
    GEAR_RARITIES.map((r) => ({ item: r, w: weights[r] })),
  );
  const slot = GEAR_SLOTS[nextInt(rng, GEAR_SLOTS.length)]!;
  return rollGear(rng, slot, rarity, balance);
}

function findItem(p: Profile, itemId: number): GearItem {
  const it = p.hero.inventory.find((x) => x.id === itemId);
  if (!it) throw new SimError('no_item', String(itemId));
  return it;
}

export function isEquipped(p: Profile, itemId: number): boolean {
  return GEAR_SLOTS.some((s) => p.hero.equipped[s] === itemId);
}

export function equipGear(p: Profile, itemId: number): void {
  const it = findItem(p, itemId);
  p.hero.equipped[it.slot] = it.id;
}

export function unequipGear(p: Profile, slot: GearSlot): void {
  if (!isSlot(slot)) throw new SimError('bad_slot', String(slot));
  delete p.hero.equipped[slot];
}

/** Destroys an item for hero XP. Equipped items come off first. */
export function scrapGear(p: Profile, itemId: number, balance: Balance): { xp: number; heroLevelUp: number | null } {
  const it = findItem(p, itemId);
  if (p.hero.equipped[it.slot] === it.id) delete p.hero.equipped[it.slot];
  p.hero.inventory = p.hero.inventory.filter((x) => x.id !== it.id);
  const xp = balance.gear.scrapXp[it.rarity];
  return { xp, heroLevelUp: grantHeroXp(p, xp, balance) };
}

/** Items of the slot and rarity that are not worn: what a merge may consume. */
export function mergeableGear(p: Profile, slot: GearSlot, rarity: GearRarity): GearItem[] {
  return p.hero.inventory.filter((x) => x.slot === slot && x.rarity === rarity && !isEquipped(p, x.id));
}

/**
 * Three identical unequipped items become one of the next rarity, in the spirit of the reference's
 * "three into one". The roll comes from the given rng so the server decides the bonus.
 */
export function mergeGear(p: Profile, slot: GearSlot, rarity: GearRarity, balance: Balance, rng: RngState): GearItem {
  if (!isSlot(slot)) throw new SimError('bad_slot', String(slot));
  if (!isRarity(rarity)) throw new SimError('bad_rarity', String(rarity));
  const next = nextRarity(rarity);
  if (!next) throw new SimError('max_rarity');
  const pool = mergeableGear(p, slot, rarity).sort((a, b) => a.bonus - b.bonus || a.id - b.id);
  if (pool.length < balance.gear.mergeCount) throw new SimError('not_enough_items', `${pool.length}/${balance.gear.mergeCount}`);
  const eaten = new Set(pool.slice(0, balance.gear.mergeCount).map((x) => x.id));
  p.hero.inventory = p.hero.inventory.filter((x) => !eaten.has(x.id));
  const item: GearItem = { id: p.hero.nextItemId++, ...rollGear(rng, slot, next, balance) };
  p.hero.inventory.push(item);
  return item;
}

export function grantHeroXp(p: Profile, xp: number, balance: Balance): number | null {
  p.hero.xp += xp;
  let up: number | null = null;
  for (;;) {
    const need = heroXpToNext(p.hero.level, balance);
    if (need === null) {
      p.hero.xp = 0;
      break;
    }
    if (p.hero.xp < need) break;
    p.hero.xp -= need;
    p.hero.level++;
    up = p.hero.level;
  }
  return up;
}

// ---------- rewards ----------

/**
 * What a finished run drops. Deterministic from the run seed and the player, so the server can
 * recompute it and two players on the same daily seed do not get identical drops.
 */
export function runRewards(
  input: { seed: number; userId: number; wavesCleared: number; won: boolean },
  balance: Balance,
): RunRewards {
  const r = balance.rewards;
  const rng = createRng((input.seed ^ Math.imul(input.userId | 0, 0x9e3779b1)) | 0);
  const n = input.wavesCleared * r.copiesPerWave + (input.won ? r.winBonusCopies : 0);
  const weights = ARCHETYPES.map((a) => ({ item: a, w: balance.unitWeights[a] }));
  const copies: Partial<Record<ArchetypeId, number>> = {};
  for (let i = 0; i < n; i++) {
    const arch = pickWeighted(rng, weights);
    copies[arch] = (copies[arch] ?? 0) + 1;
  }
  const gear = input.wavesCleared >= balance.gear.dropMinWaves ? rollDrop(rng, input.won, balance) : null;
  return {
    copies,
    heroXp: input.wavesCleared * r.heroXpPerWave + (input.won ? r.winBonusHeroXp : 0),
    medals: input.wavesCleared * r.medalsPerWave + (input.won ? r.winBonusMedals : 0),
    gear,
  };
}

/** Adds rewards to the profile in place and reports what levelled up. Copies past the cap are dropped. */
export function applyRewards(p: Profile, rewards: RunRewards, balance: Balance): RewardOutcome {
  const out: RewardOutcome = { cardLevelUps: [], heroLevelUp: null, gear: null, gearLost: false };
  for (const a of ARCHETYPES) {
    const n = rewards.copies[a] ?? 0;
    if (n <= 0) continue;
    const card = (p.cards[a] ??= { level: 1, copies: 0 });
    card.copies += n;
    for (;;) {
      const need = cardCopiesToNext(card.level, balance);
      if (need === null) {
        card.copies = 0;
        break;
      }
      if (card.copies < need) break;
      card.copies -= need;
      card.level++;
      out.cardLevelUps.push({ arch: a, level: card.level });
    }
  }
  out.heroLevelUp = grantHeroXp(p, rewards.heroXp, balance);
  p.medals = (p.medals ?? 0) + Math.max(0, rewards.medals ?? 0);
  if (rewards.gear) {
    if (p.hero.inventory.length >= balance.gear.inventoryCap) out.gearLost = true;
    else {
      out.gear = { id: p.hero.nextItemId++, ...rewards.gear };
      p.hero.inventory.push(out.gear);
    }
  }
  return out;
}

export function recordMapResult(p: Profile, mapId: string, wavesCleared: number, won: boolean, stars = 0): void {
  const m = (p.maps[mapId] ??= { won: false, bestWaves: 0, runs: 0, stars: 0 });
  m.runs++;
  m.won = m.won || won;
  m.bestWaves = Math.max(m.bestWaves, wavesCleared);
  m.stars = Math.max(m.stars ?? 0, won ? stars : 0);
}
